import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocComponentHeader } from 'commons/components/components-wrapper/poc-component-header.component';
import { PocIcon } from 'commons/components/components-wrapper/poc-icon.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { TooltipedTextComponent } from 'commons/components';
import { useTranslation } from 'core/i18n/i18n';
import { SettingsCardLayout } from 'modules/settings/settings.style';
import React from 'react';
import { usePopupManager } from 'react-popup-manager';
import { TrainingRegistrationManagementMappings } from './commons/training-registration-management-mappings.component';
import { TrainingRegistrationMappingModalComponent } from './commons/training-registration-mapping-modal.component';
import { TrainingRegistrationMappingSection } from './settings-integrations-lms-api-integration-training-registration.style';
import { TrainingRegistrationMappingData } from './types/lms-training-registration-mapping.type';

interface SettingsIntegrationsLmsApiIntegrationMappingProps {
    lmsIntegrationMappingData?: TrainingRegistrationMappingData;
    loading: boolean;
    onSaveMappings: VoidFunction;
}

export const SettingsIntegrationsLmsApiIntegrationMapping: React.FC<
    SettingsIntegrationsLmsApiIntegrationMappingProps
> = (props) => {
    const { lmsIntegrationMappingData, loading, onSaveMappings } = props;

    const { t } = useTranslation();
    const popupManager = usePopupManager();

    //open modal
    const openCreateUpdateMappingModal = () => {
        popupManager.open(TrainingRegistrationMappingModalComponent, { lmsIntegrationMappingData, onSaveMappings });
    };

    const getHeaderOptions = () => {
        const newOptions = lmsIntegrationMappingData!.mappings.flatMap((mapping) => mapping.lmsField);

        lmsIntegrationMappingData?.fallbacks.map((fallback) => {
            if (!newOptions.includes(fallback.lmsField)) {
                newOptions.push(fallback.lmsField);
            }
        });

        return newOptions;
    };

    return (
        <SettingsCardLayout interactive rounded>
            <PocComponentHeader slot="header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PocTypography element="h6" variant="title6">
                        {t('settings.integrations.lms-api-integration.mapping.title')}
                    </PocTypography>
                    {lmsIntegrationMappingData?.creationTime ? (
                        <PocTypography variant="body2">
                            {t('settings.integrations.lms-api-integration.mapping.title.last-update', {
                                date: lmsIntegrationMappingData.creationTime,
                            })}
                        </PocTypography>
                    ) : undefined}
                </div>
                <div slot="suffix" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <PocButton variant="secondary" onClick={openCreateUpdateMappingModal}>
                        {t(
                            `settings.integrations.lms-api-integration.mapping.button.${
                                lmsIntegrationMappingData?.mappings.length ? 'update' : 'create'
                            }`
                        )}
                    </PocButton>
                </div>
            </PocComponentHeader>
            <div>
                <TrainingRegistrationMappingSection>
                    {lmsIntegrationMappingData && lmsIntegrationMappingData?.mappings.length > 0 && !loading ? (
                        <>
                            <div>
                                <div style={{ paddingRight: '8px' }}>
                                    <PocTypography variant="body1" style={{ width: 'max-content' }}>
                                        {t('settings.integrations.lms-api-integration.mapping.file-reference')}
                                    </PocTypography>
                                </div>
                                <PocIcon name="file" family="filled"></PocIcon>
                                <TooltipedTextComponent
                                    i18nKey="<tooltip>{{name}}</tooltip>"
                                    tooltipText={lmsIntegrationMappingData.fileReferenceName}
                                    i18nValues={{ name: lmsIntegrationMappingData.fileReferenceName }}
                                />
                            </div>
                            <TrainingRegistrationManagementMappings
                                headers={getHeaderOptions()}
                                mappings={lmsIntegrationMappingData.mappings}
                                fallback={lmsIntegrationMappingData.fallbacks[0]}
                                disableEdit
                            />
                        </>
                    ) : (
                        <PocTypography element="p" variant="body1">
                            {t('settings.integrations.lms-api-integration.mapping.description')}
                        </PocTypography>
                    )}
                </TrainingRegistrationMappingSection>
            </div>
        </SettingsCardLayout>
    );
};
